/**
 * Calmedica-only: HGO parent ↔ subsidiaries, keyed by `account_id` (stable).
 * Links use `AccountFinal?id=<account_id>` (resolveAccount supports non-UUID account_id).
 *
 * Source: tenant_id=calmedica account rows (hgo_001 + child facilities).
 */

const HGO_PARENT = {
  account_id: 'hgo_001',
  label: 'Hospi Grand Ouest (HGO)',
};

/** Optional name aliases — used only if `account_id` is missing or for fuzzy match. */
const HGO_PARENT_NAME_ALIASES = [HGO_PARENT.label, 'Hospi Grand Ouest'];

/** Subsidiaries: canonical account_id + display label (from DB account_name). */
const HGO_CHILDREN = [
  { account_id: 'sagesse_001', label: 'Clinique Mutualiste La Sagesse' },
  { account_id: 'estuaire_001', label: "Clinique Mutualiste de l'Estuaire" },
  { account_id: 'hpca_001', label: "Hôpital Privé des Côtes d'Armor" },
  { account_id: 'porte_orient_001', label: "Clinique Mutualiste de la Porte de l'Orient" },
  { account_id: 'cmbo_001', label: 'Clinique Mutualiste de Bretagne Occidentale' },
];

const CHILD_IDS = new Set(HGO_CHILDREN.map((c) => c.account_id.toLowerCase()));

function normId(id) {
  return String(id || '')
    .trim()
    .toLowerCase();
}

function normKey(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u2019/g, "'")
    .replace(/['`´]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function isParentAccount(accountId, accountName) {
  if (normId(accountId) === normId(HGO_PARENT.account_id)) return true;
  if (!accountName) return false;
  const key = normKey(accountName);
  return HGO_PARENT_NAME_ALIASES.some((a) => normKey(a) === key);
}

function findChildByAccount(accountId, accountName) {
  const id = normId(accountId);
  if (id && CHILD_IDS.has(id)) {
    return HGO_CHILDREN.find((c) => normId(c.account_id) === id) || null;
  }
  if (!accountName) return null;
  const key = normKey(accountName);
  return HGO_CHILDREN.find((c) => normKey(c.label) === key) || null;
}

/**
 * @param {{ accountId?: string|null, accountName?: string|null, tenantId?: string|null }} p
 * @returns {{ role: 'parent', subsidiaries: { account_id: string, label: string }[] } | { role: 'subsidiary', parentLabel: string, parentAccountId: string } | null}
 */
export function getCalmedicaHgoHierarchy({ accountId, accountName, tenantId }) {
  if ((tenantId || '').toLowerCase() !== 'calmedica') return null;
  if (!accountId && !accountName) return null;

  if (isParentAccount(accountId, accountName)) {
    return { role: 'parent', subsidiaries: [...HGO_CHILDREN] };
  }

  const child = findChildByAccount(accountId, accountName);
  if (child) {
    return {
      role: 'subsidiary',
      parentLabel: HGO_PARENT.label,
      parentAccountId: HGO_PARENT.account_id,
    };
  }

  return null;
}
